import { autoinject, TaskQueue } from 'aurelia-framework';

import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import { AuditApi } from '~/api/audit-api';
import { AppRouteID } from '~/app-routes';

import {
	AcurityTable,
	AcurityTableConfig,
	PageIdentifier,
	RecordIdentifier,
} from '~/components/acurity-table/acurity-table';
import { bindAcurityTableShortcuts } from '~/components/acurity-table/acurity-table-shortcuts';
import {
	BaseDialog,
	BaseDialogModel,
	BaseDialogModelDefaults,
} from '~/components/dialog/base-dialog';
import { Notification } from '~/components/notification/notification';
import {
	ExcelExportDialog,
	ExcelExportDialogModel,
} from '~/forms/excel-export-dialog';
import { AuditResults } from '~/models/audit-model';
import { ApiCorrelationId } from '~/models/entities/api-correlation-id';
import { BlockUtils } from '~/utils/block-utils';
import { DialogUtils } from '~/utils/dialog-utils';
import { Export } from '~/utils/export';
import { FetchUtils } from '~/utils/fetch-utils';
import { KeyboardShortcut } from '~/utils/keyboard-shortcut';

export interface AuditDialogModel extends BaseDialogModel {
	auditApi?: AuditApi;
	functionName?: string;
	recordId?: number;
	queryString?: string;
	columnNames?: string[];
	tableConfig?: AcurityTableConfig;
	queryParams?: Map<string, string>;
	isAddressAudit?: boolean;
}

const AuditDialogModelDefaults: AuditDialogModel = {
	...BaseDialogModelDefaults,
	dialogSize: BaseDialog.DialogSize.EXTRA_LARGE,
	fixedFooter: true,
	headingText: 'audit.audit',
	scrollable: false,
};

@autoinject
export class AuditDialog extends BaseDialog<AuditDialogModel> {
	private static readonly DEFAULT_TABLE_COLUMN_NAMES: readonly string[] = [
		'mainElement',
		'source',
		'fieldName',
		'actionType',
		'oldValue',
		'newValue',
		'modificationBy',
		'modificationDate',
		'modificationTime',
	];

	private auditTable: AcurityTable;

	protected tableConfig: AcurityTableConfig = {
		tableColumns: [],
		multiColumnOrder: [],
		rowIdKey: '',
		defaultSortField: '',
		deletedHighlightField: '',
		defaultSortDirection: 'desc',
		isAjaxTable: false,
		isLoading: false,
	};

	private detailedAuditModel?: AuditResults;

	constructor(
		private router: Router,
		private readonly taskQueue: TaskQueue,
		protected readonly i18n: I18N,
		readonly dialogUtils?: DialogUtils,
		...rest
	) {
		super(...rest);
	}

	protected activate(model: AuditDialogModel): Promise<AuditDialogModel> {
		const result = super.activate(model, AuditDialogModelDefaults);

		return result.then(() => {
			BlockUtils.toggleAppInput(true);
			return this.initAuditDialog(model);
		});
	}

	private initAuditDialog(
		model: AuditDialogModel,
	): Promise<AuditDialogModel> {
		let promise: Promise<AuditResults>;

		if (model.recordId) {
			if (this.model.isAddressAudit) {
				promise = AuditApi.getAddressAudit(
					this.model.functionName,
					this.model.recordId,
					this.model.queryParams,
				);
			} else {
				promise = AuditApi.getAudit(
					this.model.functionName,
					this.model.recordId,
					this.model.queryParams,
				);
			}
		} else {
			promise = AuditApi.getFormAudit(
				this.model.functionName,
				this.model.queryString,
			);
		}

		return promise
			.then((detailedModel) => {
				this.tableConfig = {
					...this.tableConfig,
					...this.model.tableConfig,
				};
				let columnNames: readonly string[];
				if (
					this.model.columnNames &&
					this.model.columnNames.length > 0
				) {
					columnNames = this.model.columnNames;
				} else {
					columnNames = AuditDialog.DEFAULT_TABLE_COLUMN_NAMES;
				}

				this.tableConfig.tableColumns = [];

				columnNames.forEach((columnName) =>
					this.tableConfig.tableColumns.push(
						detailedModel.tableColumns[columnName],
					),
				);

				this.registerCorrelationIdField(detailedModel);

				this.detailedAuditModel = detailedModel;
				return this.model;
			})
			.catch((e) => {
				FetchUtils.logAndDisplayError(e);
				return this.model;
			})
			.finally(() => {
				BlockUtils.toggleAppInput(false);
			});
	}

	protected attached() {
		super.attached();

		// NOTE: Jake: 2019-07-17 - WEBUI-2246
		// To test this with lots of data, I visited:
		// http://localhost:9000/#/member-payroll-history?client=1300075&division=5&fund=GSUP&member=000074&nav-id=MEMBER&nav-view=e2e-navigation01
		// Then selected any record.
		bindAcurityTableShortcuts(this, {
			selectPreviousRecord: this.selectPreviousRecord,
			selectNextRecord: this.selectNextRecord,
			selectPreviousPage: this.selectPreviousPage,
			selectNextPage: this.selectNextPage,
			selectFirstPage: this.selectFirstPage,
			selectLastPage: this.selectLastPage, // As per WEBUI-2427, only non-ajax tables like Audit support jumping to last page
			canUseSelectRecordOrPage: () => true,
		});

		if (
			this.detailedAuditModel &&
			this.detailedAuditModel.recordSetTooLarge
		) {
			Notification.error(
				this.i18n.tr('errors.audit-record-set-too-large'),
			);
		}

		// Add PerfectScrollbar to table container
		this.taskQueue.queueMicroTask(() => {
			if (this.auditTable) {
				this.auditTable.forceInitScrollbarStyling();
			}
		});
	}

	protected detached() {
		KeyboardShortcut.unsubscribeAll(this);
	}

	private selectSpecificRecord = (
		recordIdentifier: RecordIdentifier,
	): void => {
		this.auditTable.selectSpecificRecord(recordIdentifier);
	};

	private selectFirstPage = (): void => {
		this.selectPage(PageIdentifier.FIRST);
	};

	private selectLastPage = (): void => {
		this.selectPage(PageIdentifier.LAST);
	};

	protected selectNextRecord = (): void => {
		this.selectSpecificRecord(RecordIdentifier.NEXT);
	};

	protected selectPreviousRecord = (): void => {
		this.selectSpecificRecord(RecordIdentifier.PREVIOUS);
	};

	private selectNextPage = (): void => {
		this.selectPage(PageIdentifier.NEXT);
	};

	private selectPreviousPage = (): void => {
		this.selectPage(PageIdentifier.PREVIOUS);
	};

	private selectPage = (pageIdentifier: PageIdentifier): void => {
		this.auditTable.selectSpecificPage(pageIdentifier);
	};

	private exportToCsv = async () => {
		const exportInstruction = { doExport: true, numberToExport: 0 };
		const closeResult = await this.dialogUtils
			.showCustomDialog(ExcelExportDialog, {
				numberToExport: this.detailedAuditModel.entries.length,
				recordsTotal: this.detailedAuditModel.entries.length,
			})
			.whenClosed((value) => value);
		exportInstruction.doExport = !closeResult.wasCancelled;
		if (exportInstruction.doExport) {
			exportInstruction.numberToExport = (
				closeResult.output as ExcelExportDialogModel
			).numberToExport;
			const data = this.convertAuditModelToArrayOfObjects(
				exportInstruction.numberToExport,
			);
			Export.exportToCSV(this.model.functionName + '-audit.csv', data);
		}
	};

	private convertAuditModelToArrayOfObjects = (
		numberToExport?: number,
	): object[] => {
		if (numberToExport) {
			const entriesToExport = this.detailedAuditModel.entries.slice(
				0,
				numberToExport,
			);
			return entriesToExport.map((entry) => {
				return this.tableConfig.tableColumns.reduce(
					(obj, { name, label }) => ({
						...obj,
						[label]: entry[name],
					}),
					{},
				);
			});
		}
		return [];
	};

	/**
	 * Register the correlationId column in the audit table.
	 * It enables the tooltip and the link to {@link AppRouteID.ApiAudit}
	 *
	 * @param detailedResults audit detailed results
	 */
	private registerCorrelationIdField(detailedResults: AuditResults): void {
		if (detailedResults.entries?.length > 0) {
			this.tableConfig.tableColumns.push(
				detailedResults.tableColumns[ApiCorrelationId.CORRELATION_ID],
			);
			this.tableConfig.frontEndTableColumnsOverrides = {
				...this.tableConfig.frontEndTableColumnsOverrides,
				...{
					correlationId: {
						enableToolTip: true,
						render: (fieldValue) => {
							return ApiCorrelationId.buildLink(
								this.router,
								fieldValue,
							);
						},
					},
				},
			};
		}
	}
}
